import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  MapPin, 
  Star, 
  Clock, 
  Info, 
  IndianRupee,
  ShieldCheck,
  Printer,
  ChevronRight
} from "lucide-react";
import { publicAPI } from "../api/public";
import DocumentUploader from "../components/DocumentUploader";

export default function ShopPage({ shopId }: { shopId: string }) {
  const navigate = useNavigate();
  const [shop, setShop] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadShopData();
  }, [shopId]);

  const loadShopData = async () => {
    try {
      setLoading(true);
      const data = await publicAPI.getShopInfo(shopId);
      setShop(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load shop details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-black uppercase tracking-widest text-[10px]">Opening Storefront</p>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center mb-6 border border-red-100">
           <Info className="w-10 h-10 text-red-500" />
        </div>
        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Shop Not Found</h2>
        <p className="text-slate-500 mt-2 mb-8 font-medium">{error || "This shop is not available."}</p>
        <button 
          onClick={() => navigate('/find-shops')}
          className="w-full max-w-xs bg-slate-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl"
        >
          Back to Search
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-10">
      {/* Premium Shop Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 pt-safe-top pb-20 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-between h-16 mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 text-white backdrop-blur-md active:scale-95 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-2">
             <ShieldCheck className="w-4 h-4 text-blue-200" />
             <span className="text-[10px] font-black text-blue-100 uppercase tracking-widest">Verified Partner</span>
          </div>
          <div className="w-10"></div>
        </div>

        <div className="max-w-lg mx-auto flex items-center space-x-5">
           <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center text-blue-600 text-3xl font-black shadow-2xl">
              {shop.shop_name.charAt(0).toUpperCase()}
           </div>
           <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-black text-white tracking-tight truncate leading-none mb-2">
                 {shop.shop_name}
              </h1>
              <div className="flex items-center space-x-3">
                 <div className="flex items-center text-blue-100 text-xs font-bold">
                    <Star className="w-3.5 h-3.5 text-amber-400 fill-current mr-1" />
                    4.9 (200+)
                 </div>
                 <div className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${shop.is_open ? 'bg-green-400 text-green-950' : 'bg-red-400 text-red-950'}`}>
                    {shop.is_open ? 'Open Now' : 'Closed'}
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="max-w-lg mx-auto px-4 -mt-10">
         {/* Location & Info Card */}
         <div className="bg-white rounded-[2rem] p-6 shadow-xl shadow-slate-200/50 border border-slate-100 mb-6">
            <div className="flex items-start space-x-4 mb-6">
               <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 flex-shrink-0">
                  <MapPin className="w-5 h-5" />
               </div>
               <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Address</p>
                  <p className="text-sm font-bold text-slate-700 leading-relaxed">{shop.shop_address || "Address not provided"}</p>
               </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-6 border-t border-slate-50">
               <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                     <IndianRupee className="w-4 h-4" />
                  </div>
                  <div>
                     <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">B&W</p>
                     <p className="text-sm font-black text-slate-900">₹{shop.price_per_page_bw || 2}</p>
                  </div>
               </div>
               <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center text-purple-600">
                     <IndianRupee className="w-4 h-4" />
                  </div>
                  <div>
                     <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Color</p>
                     <p className="text-sm font-black text-slate-900">₹{shop.price_per_page_color || 10}</p>
                  </div>
               </div>
            </div>
         </div>

         {/* Document Upload Area */}
         <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden mb-10">
            <div className="bg-slate-900 p-6 flex items-center justify-between">
               <div className="flex items-center space-x-3">
                  <Printer className="w-5 h-5 text-blue-400" />
                  <h2 className="text-white font-black uppercase tracking-widest text-sm">Upload Center</h2>
               </div>
               <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Secure Transfer</div>
            </div>
            
            <div className="p-2">
               <DocumentUploader shopId={shopId} shopName={shop.shop_name} />
            </div>
         </div>

         {/* Trust Footer */}
         <div className="text-center px-10 pb-10">
            <div className="flex items-center justify-center space-x-2 mb-2 opacity-30">
               <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
               <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
               <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
            </div>
            <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.3em]">
               End-to-End Encrypted by Prindox
            </p>
         </div>
      </main>
    </div>
  );
}
